import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  MapPin,
  Calendar,
  User,
  Download,
  Filter,
  RefreshCw,
  ClipboardList,
  Users,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import moment from "moment";

const ReportsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("visits");
  const [loading, setLoading] = useState(false);
  const [sellers, setSellers] = useState([]);

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedSeller, setSelectedSeller] = useState("all");
  const [selectedDocType, setSelectedDocType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Report data
  const [visitsReport, setVisitsReport] = useState(null);
  const [ordersReport, setOrdersReport] = useState(null);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/users/sellers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSellers(response.data);
    } catch (error) {
      console.error("Error fetching sellers:", error);
    }
  };

  const fetchVisitsReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedSeller !== "all") params.append("seller_id", selectedSeller);

      const response = await axios.get(`${API}/reports/visits?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVisitsReport(response.data);
    } catch (error) {
      toast.error(error?.response?.data.detail || "Error al cargar reporte de seguimiento");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedSeller !== "all") params.append("seller_id", selectedSeller);
      if (selectedDocType !== "all") params.append("doc_type", selectedDocType);
      if (selectedStatus !== "all") params.append("status", selectedStatus);

      const response = await axios.get(`${API}/reports/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrdersReport(response.data);
    } catch (error) {
      toast.error("Error al cargar reporte de pedidos");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    if (activeTab === "visits") {
      fetchVisitsReport();
    } else {
      fetchOrdersReport();
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row =>
        headers.map(h => {
          const val = row[h];
          if (val === null || val === undefined) return "";
          if (typeof val === "string" && (val.includes(",") || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }).join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Reportes</h2>
          <p className="text-sm text-slate-500">Genera reportes de seguimiento y pedidos</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="visits" className="flex items-center gap-2">
            <MapPin size={16} />
            Seguimiento
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ClipboardList size={16} />
            Pedidos
          </TabsTrigger>
        </TabsList>

        {/* Filters Section */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-slate-500" />
            <h3 className="font-medium text-slate-700">Filtros</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Vendedor</Label>
              <Select value={selectedSeller} onValueChange={setSelectedSeller}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los vendedores</SelectItem>
                  {sellers.map((seller) => (
                    <SelectItem key={seller.id} value={seller.id}>
                      {seller.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activeTab === "orders" && (
              <>
                <div className="space-y-2">
                  <Label>Tipo de Documento</Label>
                  <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="order">Pedidos</SelectItem>
                      <SelectItem value="quote">Cotizaciones</SelectItem>
                      <SelectItem value="invoice">Facturas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="approved">Aprobado</SelectItem>
                      <SelectItem value="delivered">Entregado</SelectItem>
                      <SelectItem value="cancelled">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleGenerateReport}
              disabled={loading}
              className="bg-[#0F172A] hover:bg-[#1E293B]"
            >
              {loading ? (
                <RefreshCw size={16} className="mr-2 animate-spin" />
              ) : (
                <FileText size={16} className="mr-2" />
              )}
              Generar Reporte
            </Button>
            {((activeTab === "visits" && visitsReport?.visits?.length > 0) ||
              (activeTab === "orders" && ordersReport?.documents?.length > 0)) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (activeTab === "visits") {
                      exportToCSV(visitsReport.visits, "reporte_seguimiento");
                    } else {
                      exportToCSV(ordersReport.documents, "reporte_pedidos");
                    }
                  }}
                >
                  <Download size={16} className="mr-2" />
                  Exportar CSV
                </Button>
              )}
          </div>
        </div>

        {/* Visits Report */}
        <TabsContent value="visits" className="space-y-4">
          {visitsReport && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MapPin size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Visitas</p>
                      <p className="text-2xl font-semibold text-slate-800">{visitsReport.total}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Users size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Vendedores Activos</p>
                      <p className="text-2xl font-semibold text-slate-800">
                        {Object.keys(visitsReport.summary?.by_seller || {}).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <TrendingUp size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Tipos de Seguimiento</p>
                      <p className="text-2xl font-semibold text-slate-800">
                        {Object.keys(visitsReport.summary?.by_reason || {}).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visits Table */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Vendedor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tipo Seguimiento</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ubicación</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Comentarios</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {visitsReport.visits.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                            No hay visitas registradas con los filtros seleccionados
                          </td>
                        </tr>
                      ) : (
                        visitsReport.visits.map((visit) => (
                          <tr key={visit.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-slate-800">{moment(visit.created_at).format('DD-MM-YYYY')}</div>
                              <div className="text-slate-500">{moment(visit.created_at).format('h:mm:ss A')}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">{visit.vendedor}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{visit.cliente}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                {visit.reason}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {visit.latitude ? (
                                <a
                                  href={`https://maps.google.com/?q=${visit.latitude},${visit.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  <MapPin size={14} />
                                  Ver mapa
                                </a>
                              ) : (
                                <span className="text-slate-400">Sin ubicación</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                              {visit.comments || "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!visitsReport && (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <MapPin size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">Reporte de Seguimiento</h3>
              <p className="text-slate-500 mb-4">
                Selecciona los filtros y genera el reporte para ver las visitas a clientes
              </p>
            </div>
          )}
        </TabsContent>

        {/* Orders Report */}
        <TabsContent value="orders" className="space-y-4">
          {ordersReport && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ClipboardList size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Pedidos</p>
                      <p className="text-2xl font-semibold text-slate-800">
                        {
                          ordersReport.documents.filter(r => r.tipo === 'order').reduce((sum, r) => sum + Number(r.total || 0), 0)
                            .toLocaleString("es-DO", { minimumFractionDigits: 2 })
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <FileText size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Cotizaciones</p>
                      <p className="text-2xl font-semibold text-slate-800">
                        {
                          ordersReport.documents.filter(r => r.tipo === 'quote').reduce((sum, r) => sum + Number(r.total || 0), 0)
                            .toLocaleString("es-DO", { minimumFractionDigits: 2 })
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <FileText size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Facturas</p>
                      <p className="text-2xl font-semibold text-slate-800">
                        {
                          ordersReport.documents.filter(r => r.tipo === 'invoice').reduce((sum, r) => sum + Number(r.total || 0), 0)
                            .toLocaleString("es-DO", { minimumFractionDigits: 2 })
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <TrendingUp size={20} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total</p>
                      <p className="text-xl font-semibold text-slate-800">
                        RD$ {ordersReport.summary.total_amount?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Orders Table */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Número</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Vendedor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Items</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {ordersReport.documents.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                            No hay documentos con los filtros seleccionados
                          </td>
                        </tr>
                      ) : (
                        ordersReport.documents.map((doc) => (
                          <tr key={doc.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm font-medium text-slate-800">{doc.numero}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{doc.fecha}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${doc.tipo_raw === "order" ? "bg-blue-100 text-blue-700" :
                                doc.tipo_raw === "quote" ? "bg-amber-100 text-amber-700" :
                                  "bg-emerald-100 text-emerald-700"
                                }`}>
                                {doc.tipo}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">{doc.cliente}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{doc.vendedor}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{doc.items_count}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-slate-800">
                              RD$ {doc.total?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${doc.estado_raw === "pending" ? "bg-amber-100 text-amber-700" :
                                doc.estado_raw === "approved" ? "bg-blue-100 text-blue-700" :
                                  doc.estado_raw === "delivered" ? "bg-emerald-100 text-emerald-700" :
                                    "bg-red-100 text-red-700"
                                }`}>
                                {doc.estado}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!ordersReport && (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <ClipboardList size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">Reporte de Pedidos</h3>
              <p className="text-slate-500 mb-4">
                Selecciona los filtros y genera el reporte para ver los pedidos, cotizaciones y facturas
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
